import { Fragment } from 'react';
import { POPOVER_Z_INDEX } from '@constants/z-indices';
import { EndorsementsButtonController } from '@ContractBuilder/contract-builder-header/modules/endorsements-button';
import { RenewalsButtonController } from '@ContractBuilder/contract-builder-header/modules/renewals-button';
import { useEntityStore } from '@ContractBuilder/store';
import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import { DocumentMode } from '@helpers/document-mode';
import { getDocumentMode } from '@helpers/getDocumentMode';
import { PaperClipIcon } from '@heroicons/react/outline';
import { ChevronDownIcon } from '@heroicons/react/solid';
import { useFeatureFlags } from '@src/hooks';
import clsx from 'clsx';

export const LinkedDocsController = () => {
  const submission = useEntityStore(({ submission }) => submission);
  const getFeatureFlagState = useFeatureFlags();

  const hasEndorsementsFeature = getFeatureFlagState('Endorsements');

  const documentMode = getDocumentMode();
  const isEnabled = submission?.status === 'FINAL' && documentMode.mode === DocumentMode.SUBMISSIONS;

  if (!submission || !hasEndorsementsFeature || !isEnabled) {
    return null;
  }

  return (
    <Popover>
      {({ open }) => (
        <>
          <div className="relative shrink-0">
            <div className="divide-info-600 border-info-300 inline-flex divide-x rounded-md border shadow-xs">
              <div className="divide-info-300 relative z-0 inline-flex divide-x rounded-md shadow-xs">
                <div
                  className={clsx(
                    'text-info-700 relative inline-flex items-center rounded-l-md bg-white py-1 pr-4 pl-3 shadow-xs',
                  )}
                >
                  <PaperClipIcon className="h-5 w-5" />
                  <p className="ml-2.5 text-sm font-medium">Linked docs</p>
                </div>
                <PopoverButton
                  className={clsx(
                    'text-info-700 hover:bg-info-100 relative inline-flex h-9 items-center rounded-l-none rounded-r-md p-2 py-1 text-sm font-medium focus:z-10 focus:outline-hidden',
                  )}
                  data-testid="linked-docs-button"
                >
                  <span className="sr-only">Linked docs</span>
                  <ChevronDownIcon className="text-info-400 h-5 w-5" aria-hidden="true" />
                </PopoverButton>
              </div>
            </div>
            <Transition
              show={open}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <PopoverPanel
                className={clsx(
                  'absolute',
                  'right-0',
                  'mt-2',
                  'w-[752px]',
                  'origin-top-right',
                  'divide-y',
                  'divide-info-200',
                  'max-h-[calc(100vh-8rem)]',
                  'overflow-y-auto',
                  'rounded-md',
                  'bg-white',
                  'shadow-xl',
                  'ring-1',
                  'ring-black/5',
                  'focus:outline-hidden',
                  POPOVER_Z_INDEX,
                )}
              >
                <EndorsementsButtonController submission={submission} />
                <RenewalsButtonController submission={submission} />
              </PopoverPanel>
            </Transition>
          </div>
        </>
      )}
    </Popover>
  );
};
